/**
 * GuidedOrderReferrerSection
 *
 * Display and (for clinic admins, pre-capture) edit the referrer assigned
 * to a guided order. Used inside GuidedOrderDetailSheet.
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Users, Pencil } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useClinic } from '@/contexts/ClinicContext'
import { useUpdateGuidedOrderReferrer } from '@/hooks/useGuidedOrders'
import ReferringProviderSelect from '@/components/commissions/ReferringProviderSelect'
import type { GuidedOrder } from '@/types/guidedOrder'

const ROLE_LABELS: Record<string, string> = {
  practitioner: 'Practitioner',
  clinic_staff: 'Clinic Staff',
  clinic_admin: 'Clinic Admin',
  admin: 'Admin',
  super_admin: 'System Admin',
}

interface Props {
  order: GuidedOrder
}

export function GuidedOrderReferrerSection({ order }: Props) {
  const { isAdmin } = useClinic()
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)
  const [formValue, setFormValue] = useState<{
    referringUserId: string | null
    commissionRateBps: number | null
  }>({
    referringUserId: order.referring_user_id,
    commissionRateBps: order.commission_rate_bps,
  })

  const mutation = useUpdateGuidedOrderReferrer()

  const canEdit = isAdmin && order.payment_status !== 'captured'

  const referrerName = order.referrer
    ? `${order.referrer.first_name} ${order.referrer.last_name}`
    : null
  const customRoleNames = (order.referrer?.userRoles || [])
    .filter(ur => !ur.clinic_id || ur.clinic_id === order.clinic_id)
    .map(ur => ur.role?.name)
    .filter((name): name is string => !!name)
  const fallbackRoleLabel = order.referrer?.role
    ? ROLE_LABELS[order.referrer.role] ||
      order.referrer.role.replace(/_/g, ' ')
    : null
  const roleLabels = customRoleNames.length
    ? customRoleNames
    : fallbackRoleLabel
      ? [fallbackRoleLabel]
      : []

  const openEdit = () => {
    setFormValue({
      referringUserId: order.referring_user_id,
      commissionRateBps: order.commission_rate_bps,
    })
    setEditing(true)
  }

  const save = () => {
    mutation.mutate(
      {
        orderId: order.id,
        referringUserId: formValue.referringUserId,
        commissionRateBps: formValue.commissionRateBps,
      },
      {
        onSuccess: () => {
          setEditing(false)
          toast({ title: 'Referrer updated' })
        },
        onError: (error: Error) => {
          toast({
            variant: 'destructive',
            title: 'Could not update referrer',
            description: error.message,
          })
        },
      },
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            Referrer
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 px-2"
                onClick={openEdit}
              >
                <Pencil className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {order.referring_user_id && referrerName ? (
            <div className="flex justify-between items-start gap-4 py-1.5">
              <div className="space-y-1">
                <div className="text-sm font-medium text-slate-900">
                  {referrerName}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {roleLabels.map(label => (
                    <Badge key={label} variant="outline" className="capitalize">
                      {label}
                    </Badge>
                  ))}
                  {order.referrer?.email && (
                    <span className="text-xs text-slate-500">
                      {order.referrer.email}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500">Commission rate</div>
                <div className="text-sm font-medium text-slate-900">
                  {order.commission_rate_bps != null
                    ? `${(order.commission_rate_bps / 100).toFixed(1)}%`
                    : 'Default'}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No referrer assigned.</p>
          )}
          {!canEdit && isAdmin && order.payment_status === 'captured' && (
            <p className="text-xs text-slate-400 mt-2">
              Referrer is locked once payment has been captured.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Referrer</DialogTitle>
            <DialogDescription>
              Pick a clinic user to attribute this order to, or clear it.
              Changes are blocked once payment is captured.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <ReferringProviderSelect
              clinicId={order.clinic_id}
              value={formValue}
              onChange={val =>
                setFormValue({
                  referringUserId: val.referringUserId,
                  commissionRateBps: val.commissionRateBps,
                })
              }
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setFormValue({
                  referringUserId: null,
                  commissionRateBps: null,
                })
              }}
            >
              Clear
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={
                mutation.isPending ||
                (!!formValue.referringUserId &&
                  formValue.commissionRateBps == null)
              }
            >
              {mutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default GuidedOrderReferrerSection
